import { Link } from "react-router-dom"
import { AlertCircle, CheckCircle2, FileSignature } from "lucide-react"
import type { LeaseIntakeRun } from "@/app/api/leaseIntakes"
import type { ClientTheme } from "@/app/lib/themes"
import { primaryButton, secondaryButton } from "@/app/lib/buttonStyles"
import { Card, PanelLabel, headStyle, mutedText } from "@/app/pages/LeaseIntakePage/shared"

// Committed (or discarded) run: LeaseImportService's summary, the conflicts
// it reported but did not apply, and per-row errors.
export function CommittedPanel({ run, theme }: { run: LeaseIntakeRun; theme: ClientTheme }) {
  const summary = run.summary
  const discarded = run.status === "discarded"

  return (
    <Card theme={theme}>
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 size={16} style={{ color: discarded ? theme.textDim : theme.brandTeal }} />
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "14px",
            fontWeight: 600,
            color: theme.textPrimary,
          }}
        >
          {discarded
            ? "This import was discarded — nothing was added to the Lease Schedule."
            : "Committed to the Lease Schedule"}
        </span>
      </div>

      {summary && (
        <>
          <PanelLabel theme={theme}>Import summary</PanelLabel>
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Inserted" value={summary.inserted} color={theme.brandTeal} theme={theme} />
            <Stat
              label="Updated"
              value={summary.updated}
              color={theme.brandPrimary}
              theme={theme}
            />
            <Stat label="Skipped" value={summary.skipped} color={theme.textMuted} theme={theme} />
          </div>

          {summary.conflicts.length > 0 && (
            <div className="mt-6">
              <SubHead
                theme={theme}
                count={summary.conflicts.length}
                noun="conflict"
                hint="Existing values were kept. Edit the lease on the schedule to change them."
              />
              <SimpleTable
                theme={theme}
                headers={["Lessor", "Field", "Existing", "Incoming"]}
                rows={summary.conflicts.map((c, i) => ({
                  key: `${c.lease_id ?? c.key ?? ""}-${c.field}-${i}`,
                  cells: [c.lessor ?? "—", c.field, c.existing ?? "—", c.incoming ?? "—"],
                }))}
              />
            </div>
          )}

          {summary.errors.length > 0 && (
            <div className="mt-6">
              <SubHead theme={theme} count={summary.errors.length} noun="error" />
              <SimpleTable
                theme={theme}
                headers={["Lessor", "Reason"]}
                rows={summary.errors.map((e, i) => ({
                  key: `err-${i}`,
                  cells: [e.lessor ?? "—", e.reason],
                }))}
              />
            </div>
          )}
        </>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/leases" className="btn" style={primaryButton(theme)}>
          <FileSignature size={14} /> Back to Lease Schedule
        </Link>
        <Link to="/leases/intake" className="btn" style={secondaryButton(theme)}>
          Import another document
        </Link>
      </div>
    </Card>
  )
}

function SubHead({
  theme,
  count,
  noun,
  hint,
}: {
  theme: ClientTheme
  count: number
  noun: string
  hint?: string
}) {
  return (
    <div className="mb-2">
      <div className="flex items-center gap-1.5">
        <AlertCircle size={14} style={{ color: theme.brandAccent }} />
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "12px",
            fontWeight: 600,
            color: theme.brandAccent,
          }}
        >
          {count} {noun}
          {count === 1 ? "" : "s"}
        </span>
      </div>
      {hint && <div style={{ ...mutedText(theme, "11px"), marginTop: "2px" }}>{hint}</div>}
    </div>
  )
}

function SimpleTable({
  theme,
  headers,
  rows,
}: {
  theme: ClientTheme
  headers: string[]
  rows: { key: string; cells: string[] }[]
}) {
  return (
    <div
      className="rounded-lg overflow-auto"
      style={{ border: `1px solid ${theme.borderColor}`, maxHeight: "50vh" }}
    >
      <table className="w-full border-collapse">
        <thead>
          <tr
            style={{
              backgroundColor: theme.bgSurface2,
              borderBottom: `1px solid ${theme.borderColor}`,
            }}
          >
            {headers.map((h) => (
              <th key={h} className="px-4 py-2 text-left" style={headStyle(theme)}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
              {row.cells.map((cell, i) => (
                <td
                  key={i}
                  className="px-4 py-2 align-top"
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "12px",
                    color: i === 0 ? theme.textPrimary : theme.textMuted,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Stat({
  label,
  value,
  color,
  theme,
}: {
  label: string
  value: number
  color: string
  theme: ClientTheme
}) {
  return (
    <div
      className="rounded-lg p-4"
      style={{ backgroundColor: theme.bgSurface2, border: `1px solid ${theme.borderColor}` }}
    >
      <div style={{ fontFamily: "var(--font-data)", fontSize: "28px", fontWeight: 600, color }}>
        {value}
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "11px",
          fontWeight: 600,
          color: theme.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginTop: "2px",
        }}
      >
        {label}
      </div>
    </div>
  )
}
